import { useEffect, useState } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Logo } from '@/components/shared/Logo'
import { adminApi } from '@/api/admin'
import { NotificationBell } from '@/components/shared/NotificationBell'
import { LayoutDashboard, CalendarDays, CalendarRange, BookOpen, LogOut, Settings, Star, Menu, X } from 'lucide-react'

export default function TrainerLayout() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  // Below md the menu is a drawer that slides over the page, so the page itself gets the full width of a phone.
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    if (!mobileOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMobileOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [mobileOpen])

  // Growing past the phone breakpoint (rotating a tablet, resizing a window) puts the sidebar back in the page, so
  // don't leave the drawer flagged as open underneath it.
  useEffect(() => {
    const query = window.matchMedia('(min-width: 768px)')
    const onChange = (e: MediaQueryListEvent) => { if (e.matches) setMobileOpen(false) }
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const itemBase = 'flex items-center rounded-lg text-sm font-medium transition-all'
  const itemActive = 'bg-white/15 text-white'
  const itemInactive = 'text-slate-400 hover:bg-white/8 hover:text-slate-200'

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `${itemBase} gap-3 px-4 py-2.5 w-full ${isActive ? itemActive : itemInactive}`

  const closeMenu = () => setMobileOpen(false)

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Mobile backdrop */}
      {mobileOpen && <div className="fixed inset-0 z-40 bg-black/50 md:hidden" onClick={closeMenu} aria-hidden="true" />}

      {/* Sidebar: a drawer on mobile, in the page on desktop */}
      <aside
        aria-label="Trainer menu"
        className={[
          'fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 flex flex-col shrink-0',
          'transition-transform duration-200',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
          'md:relative md:inset-auto md:z-auto md:translate-x-0',
        ].join(' ')}
      >
        {/* Logo */}
        <div className="px-6 py-5 border-b border-white/8 flex items-center justify-between">
          <Logo size={40} theme="dark" />
          <button
            type="button"
            onClick={closeMenu}
            className="md:hidden text-slate-400 hover:text-white p-1 rounded-lg cursor-pointer"
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>

        {/* Nav: following a link closes the drawer */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto" onClick={closeMenu}>
          <NavLink to="/trainer/overview" className={navClass}>
            <LayoutDashboard size={17} />
            My Training
          </NavLink>
          <NavLink to="/trainer/schedule" className={navClass}>
            <CalendarDays size={17} />
            My Schedule
          </NavLink>
          <NavLink to="/trainer/calendar" className={navClass}>
            <CalendarRange size={17} />
            My Calendar
          </NavLink>
          <NavLink to="/trainer/courses" className={navClass}>
            <BookOpen size={17} />
            My Courses
          </NavLink>
          <NavLink to="/trainer/feedback" className={navClass}>
            <Star size={17} />
            My Feedback
          </NavLink>
        </nav>

        {/* Bottom */}
        <div className="px-3 py-2 border-t border-white/8 space-y-0.5">
          <NavLink
            to="/trainer/account"
            onClick={closeMenu}
            className={({ isActive }) =>
              `${itemBase} gap-2.5 px-3 py-1.5 w-full ${isActive ? itemActive : itemInactive}`
            }
          >
            <span className="shrink-0 w-6 h-6 rounded-full bg-indigo-500 flex items-center justify-center text-white text-[10px] font-semibold uppercase">
              {user?.email?.[0] ?? '?'}
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-xs font-medium text-slate-200 truncate">{user?.email}</span>
            </span>
            <Settings size={13} className="shrink-0 text-slate-500" />
          </NavLink>

          <div className="flex items-center gap-1">
            <NotificationBell api={adminApi} panelSide="right" />
            <button
              onClick={handleLogout}
              className={`${itemBase} cursor-pointer ${itemInactive} gap-2 px-3 py-1.5 flex-1`}
            >
              <LogOut size={14} />
              <span className="text-xs">Sign out</span>
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 flex flex-col overflow-hidden">
        {/* Mobile top bar: the menu button and the logo (the desktop sidebar already shows both) */}
        <header className="md:hidden h-12 bg-white border-b border-slate-200 flex items-center gap-3 px-4 shrink-0">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="p-1.5 -ml-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 cursor-pointer"
            aria-label="Open menu"
          >
            <Menu size={20} />
          </button>
          <Logo size={28} theme="light" />
        </header>

        <div className="flex-1 overflow-auto">
          <div className="max-w-5xl mx-auto px-4 py-6 sm:px-6 md:px-10 md:py-10">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  )
}
